import { animate, style, transition, trigger } from '@angular/animations';
import { DatePipe, NgClass, NgFor, NgIf, NgStyle } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, NavigationEnd, Router, RouterModule, RouterOutlet } from '@angular/router';
import { devstroupeAnimations } from '@devstroupe/animations';
import { DevstroupeFullscreenComponent } from '@devstroupe/components/fullscreen';
import { DevstroupeLoadingBarComponent } from '@devstroupe/components/loading-bar';
import { DevstroupeNavigationItem, DevstroupeNavigationService, DevstroupeVerticalNavigationComponent } from '@devstroupe/components/navigation';
import { DevstroupeMediaWatcherService } from '@devstroupe/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserComponent } from 'app/layout/common/user/user.component';
import { UserModel } from 'app/models/user.model';
import { ApiService } from 'app/shared/apis/api.service';
import { debounceTime, filter, Subject, switchMap, takeUntil } from 'rxjs';

@Component({
    selector: 'home-layout',
    templateUrl: './home.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    styles: [
        `
            home-layout {
                .devstroupe-vertical-navigation-content {
                    display: flex;
                    flex-direction: column;
                }
            }
        `,
    ],
    imports: [
        NgStyle,
        DevstroupeLoadingBarComponent,
        DevstroupeVerticalNavigationComponent,
        MatButtonModule,
        MatIconModule,
        UserComponent,
        NgIf,
        RouterOutlet,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        NgClass,
        RouterModule,
        NgFor,
        MatTooltipModule,
    ],
    animations: devstroupeAnimations,
    // animations: [
    //     trigger('fadeInOut', [
    //         transition(':enter', [
    //             style({ opacity: 0 }), // Inicialmente invisível
    //             animate('100ms ease-in', style({ opacity: 1 })), // Transição para visível
    //         ]),
    //         transition(':leave', [
    //             animate('100ms ease-out', style({ opacity: 0 })), // Transição para invisível
    //         ]),
    //     ]),
    // ],
})
export class HomeLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    navigation: Navigation;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    navigationData: DevstroupeNavigationItem[];
    hovered;
    config = {
        logo: 'assets/images/logo/prehox-logo-white.png',
        class: 'bg-white',
    };

    user: UserModel;
    menuItems = [];

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _devstroupeMediaWatcherService: DevstroupeMediaWatcherService,
        private _devstroupeNavigationService: DevstroupeNavigationService,
        private _cdr: ChangeDetectorRef,
    ) {}
    currentDateTime: Date = new Date();
    private intervalId: any;
    searchInputControl: UntypedFormControl = new UntypedFormControl();
    searchEnabled: boolean = true;
    currentUrl: string = '';

    /**
     * Getter para verificar se o search deve estar habilitado
     */
    get shouldShowSearch(): boolean {
        // Sempre verificar a URL atual para garantir que está atualizado
        const currentUrl = this._router.url;

        // Verificar se alguma rota na hierarquia tem showSearch configurado
        let route = this._activatedRoute;
        let showSearch = false;

        // Verificar todas as rotas na hierarquia (filhos e pais)
        const allRoutes: any[] = [];
        let tempRoute = route;
        while (tempRoute) {
            allRoutes.push(tempRoute);
            tempRoute = tempRoute.parent;
        }

        // Verificar também as rotas filhas
        tempRoute = route;
        while (tempRoute.firstChild) {
            tempRoute = tempRoute.firstChild;
            allRoutes.push(tempRoute);
        }

        // Verificar se alguma rota tem showSearch
        for (const r of allRoutes) {
            if (r.snapshot?.data?.['showSearch']) {
                showSearch = true;
                break;
            }
        }

        console.log('🔍 shouldShowSearch:', {
            currentUrl,
            showSearch,
            routesChecked: allRoutes.length,
            routeData: allRoutes.map(r => r.snapshot?.data)
        });

        return showSearch;
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        ApiService.userCurrent.pipe(takeUntil(this._unsubscribeAll)).subscribe((user: UserModel) => {
            this.user = user;
        });
        // Subscribe to navigation data
        this._navigationService.navigation$.pipe(takeUntil(this._unsubscribeAll)).subscribe((navigation: Navigation) => {
            this.navigation = navigation;
            this.setNavigationData();
        });

        // Verificar rota inicial
        setTimeout(() => this._checkSearchEnabled(), 100);

        // Atualizar searchEnabled quando a rota mudar
        this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll),
            )
            .subscribe((event: NavigationEnd) => {
                console.log('🚀 NavigationEnd event:', event.url);
                // Pequeno delay para garantir que a rota foi atualizada
                setTimeout(() => {
                    this._checkSearchEnabled();
                    // Forçar detecção de mudanças
                    this._cdr.markForCheck();
                }, 100);
            });

        this._activatedRoute.data.pipe(takeUntil(this._unsubscribeAll)).subscribe(({ config }) => {
            if (config) {
                this.config = config;
            }
        });

        this.searchInputControl.valueChanges
            .pipe(
                takeUntil(this._unsubscribeAll),
                debounceTime(350),
                switchMap((query) => {
                    this._router.navigate([], {
                        relativeTo: this._activatedRoute,
                        queryParams: {
                            text: query ? query : null,
                        },
                        queryParamsHandling: 'merge',
                    });
                    return query;
                }),
            )
            .subscribe();

        this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll),
            )
            .subscribe((event) => {
                if (!this._activatedRoute.snapshot.queryParams['text']) {
                    this.searchInputControl.setValue('', { emitEvent: false });
                }
            });
        // Subscribe to media changes
        this._devstroupeMediaWatcherService.onMediaChange$.pipe(takeUntil(this._unsubscribeAll)).subscribe(({ matchingAliases }) => {
            // Check if the screen is small
            this.isScreenSmall = !matchingAliases.includes('md');
        });
        this.updateDateTime();
        this.intervalId = setInterval(() => this.updateDateTime(), 1000);
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        clearInterval(this.intervalId);
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    private updateDateTime(): void {
        const now = new Date();
        // const options: Intl.DateTimeFormatOptions = {
        //     weekday: 'long',
        //     year: 'numeric',
        //     month: 'long',
        //     day: 'numeric',
        //     hour: '2-digit',
        //     minute: '2-digit',
        //     second: '2-digit',
        //     hour12: false,
        // };
        this.currentDateTime = now;
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._devstroupeNavigationService.getComponent<DevstroupeVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    setNavigationData(): void {
        this.navigationData = [];
    }

    /**
     * Verifica se o search deve estar habilitado baseado na rota atual
     */
    private _checkSearchEnabled(): void {
        this.currentUrl = this._router.url;

        // Verificar se alguma rota na hierarquia tem showSearch configurado (mesmo conceito da home)
        let route = this._activatedRoute;
        let showSearch = false;
        let ignoreSearchMobile = false;

        // Verificar todas as rotas na hierarquia (filhos e pais)
        while (route) {
            if (route.snapshot?.data?.['showSearch']) {
                showSearch = true;
            }
            if (route.snapshot?.data?.['ignoreSearchMobile']) {
                ignoreSearchMobile = true;
            }
            route = route.parent;
        }

        // Verificar também as rotas filhas
        route = this._activatedRoute;
        while (route.firstChild) {
            route = route.firstChild;
            if (route.snapshot?.data?.['showSearch']) {
                showSearch = true;
            }
            if (route.snapshot?.data?.['ignoreSearchMobile']) {
                ignoreSearchMobile = true;
            }
        }

        // Habilitar busca apenas quando showSearch estiver configurado (e não estiver oculto no mobile)
        const newValue = showSearch && !(ignoreSearchMobile && this.isScreenSmall);

        // Sempre atualizar para garantir que está correto
        if (this.searchEnabled !== newValue) {
            this.searchEnabled = newValue;
            console.log('🔍 Search updated:', {
                currentUrl: this.currentUrl,
                showSearch,
                searchEnabled: this.searchEnabled
            });
            this._cdr.markForCheck();
            this._cdr.detectChanges();
        }
    }
}
